import React, { useMemo } from 'react';
import './CharacterActionEconomyPanel.css';
import { buildCharacterActionEconomyViewModel } from '../utils/CharacterActionEconomyPresentation';

const CharacterActionEconomyPanel = ({
  subject = null,
  viewModel = null,
  title = 'Action Economy',
  showTitle = true,
  emptyMessage = 'No derived combat actions available.',
  className = '',
  compact = false,
  presentation = false
}) => {
  const resolvedViewModel = useMemo(() => {
    if (viewModel && typeof viewModel === 'object') {
      return viewModel;
    }

    return buildCharacterActionEconomyViewModel(subject);
  }, [subject, viewModel]);

  const visibleSections = Array.isArray(resolvedViewModel?.sections)
    ? resolvedViewModel.sections.filter((section) => Array.isArray(section.entries) && section.entries.length > 0)
    : [];
  const rootClassName = [
    'character-action-economy-panel',
    compact ? 'character-action-economy-panel--compact' : '',
    presentation ? 'character-action-economy-panel--presentation' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClassName}>
      {showTitle && (
        <div className="character-action-economy-panel__header">
          <h3>{title}</h3>
          {resolvedViewModel?.totalEntryCount > 0 && (
            <span className="character-action-economy-panel__count">
              {resolvedViewModel.totalEntryCount} derived option{resolvedViewModel.totalEntryCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      {resolvedViewModel?.movementSummary && (
        <div className="character-action-economy-panel__movement">
          <span className="character-action-economy-panel__movement-label">Movement</span>
          <span className="character-action-economy-panel__movement-value">{resolvedViewModel.movementSummary}</span>
        </div>
      )}

      {visibleSections.length === 0 ? (
        <div className="character-action-economy-panel__empty">{emptyMessage}</div>
      ) : (
        <>
          {presentation && visibleSections.length > 1 && (
            <nav className="character-action-economy-panel__nav" aria-label="Action types">
              {visibleSections.map((section) => (
                <a key={section.key} href={`#action-economy-${section.key}`}>{section.title} · {section.count}</a>
              ))}
            </nav>
          )}
          <div className="character-action-economy-panel__sections">
            {visibleSections.map((section) => {
              const sectionHeader = (
                <div className="character-action-economy-panel__section-header">
                  <h4>{section.title}</h4>
                  <span>{section.count}</span>
                </div>
              );
              const entries = (
                <div className="character-action-economy-panel__entries">
                  {section.entries.map((entry) => (
                    <article key={entry.key} className={`character-action-economy-panel__entry character-action-economy-panel__entry--${entry.type || 'action'}`}>
                      <div className="character-action-economy-panel__entry-name">{entry.name}</div>
                      {Array.isArray(entry.meta) && entry.meta.length > 0 && (
                        <div className="character-action-economy-panel__entry-meta">
                          {presentation
                            ? entry.meta.map((meta) => <span key={meta}>{meta}</span>)
                            : entry.meta.join(' • ')}
                        </div>
                      )}
                      {entry.description && (presentation ? (
                        <details className="character-action-economy-panel__entry-rules">
                          <summary>{entry.type === 'spell' ? 'Spell summary' : 'Rules & resolution'}</summary>
                          <div className="character-action-economy-panel__entry-description">{entry.description}</div>
                        </details>
                      ) : (
                        <div className="character-action-economy-panel__entry-description">{entry.description}</div>
                      ))}
                    </article>
                  ))}
                </div>
              );

              return presentation ? (
                <details key={section.key} id={`action-economy-${section.key}`} className="character-action-economy-panel__section" open>
                  <summary>{sectionHeader}</summary>
                  {entries}
                </details>
              ) : (
                <section key={section.key} className="character-action-economy-panel__section">
                  {sectionHeader}
                  {entries}
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CharacterActionEconomyPanel;
